import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  IconButton,
  Tooltip,
  MenuItem,
  Select,
  Typography,
  useTheme,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import CodeMirror from "@uiw/react-codemirror";
import { githubLight, githubDark } from '@uiw/codemirror-theme-github';
import { javascript } from "@codemirror/lang-javascript";
import { python } from "@codemirror/lang-python";
import { sql, SQLite } from "@codemirror/lang-sql";
import { java } from "@codemirror/lang-java";
import { json } from "@codemirror/lang-json";

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import CheckIcon from '@mui/icons-material/Check';

import codeSnippets from "../config/codeEditorConfig";

const useStyles = makeStyles((theme) => ({
  topBar: {
    position: "relative",
    display: "flex",
    justifyContent: "space-between",
    background:
      theme.palette.mode === 'dark'
        ? 'linear-gradient(to bottom,rgb(73, 73, 73), #2e2e2e)'
        : 'linear-gradient(180deg, rgb(255, 255, 255), #f2f2f2)',
    margin: "10px 12px 0 12px",
    padding: "10px 10px",
  },
  topBarLeft: {
    marginLeft: "22px",
  },
  topBarRight: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginRight: "22px",

  },
  editorContainer: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    margin: 12,
    marginBottom: 0,
    marginTop: 0,
    animation: `$fadeIn 0.4s forwards`,
  },
  "@keyframes fadeIn": {
    "0%": { opacity: 0 },
    "100%": { opacity: 1 },
  },
  codeMirror: {
    flex: 1,
    fontSize: "16px",
    fontFamily: "monospace",
  },
  selectSmall: {
    height: "32px",
  },
}));

const CodeEditorV2 = ({ onChange, initialCode, language: initialLanguage }) => {
  const theme = useTheme();
  const classes = useStyles();
  const isDarkMode = theme.palette.mode === 'dark';

  const randomCode = codeSnippets[Math.floor(Math.random() * codeSnippets.length)];
  const defaultCode = typeof randomCode === 'string' ? randomCode : randomCode.code;
  const [code, setCode] = useState(initialCode || defaultCode);
  const [language, setLanguage] = useState(initialLanguage || 'javascript');
  const [copied, setCopied] = useState(false);
  const [paste, setPaste] = useState(false);
  const viewRef = useRef(null);

  useEffect(() => {
    if (viewRef.current) {
      viewRef.current.view = viewRef.current.editor.view;
    }
    onChange(code, language);
  }, [code, language, onChange]);

  useEffect(() => {
    if (initialLanguage && initialLanguage !== language) {
      setLanguage(initialLanguage);
    }
  }, [initialLanguage]);

  const handleCopy = () => {
    const view = viewRef.current?.view;
    if (view) {
      const text = view.state.doc.toString();
      navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 500);
    }
  };

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      const view = viewRef.current?.view;
      if (view) {
        view.dispatch({ changes: { from: 0, to: view.state.doc.length, insert: text } });
        if (onChange) onChange(text);
      }
      setPaste(true);
      setTimeout(() => setPaste(false), 500);
    } catch (error) {
      console.error("Failed to paste clipboard contents:", error);
    }
  };

  const handleLanguageChange = (event) => {
    const newLang = event.target.value;
    setLanguage(newLang);
    onChange(code, newLang);
  };

  const handleChange = (newCode) => {
    setCode(newCode);
    onChange(newCode, language);
  };

  const getLanguageExtension = (lang) => {
    switch (lang) {
      case 'javascript': return javascript();
      case 'python': return python();
      case 'java': return java();
      case 'json': return json();
      case 'sql': return sql({ dialect: SQLite });
      default: return javascript();
    }
  };

  return (
    <Box>
      <Box className={classes.topBar}>
        <Box className={classes.topBarLeft} />
        <Box className={classes.topBarRight}>
          <Box sx={{ marginRight: "16px", flexDirection: "row", display: "flex", alignItems: "center", gap: "8px" }}>
            <Typography variant="body2">Language:</Typography>
            <Select
              value={language}
              onChange={handleLanguageChange}
              size="small"
              className={classes.selectSmall}
            >
              <MenuItem value="javascript">JavaScript</MenuItem>
              <MenuItem value="python">Python</MenuItem>
              <MenuItem value="java">Java</MenuItem>
              <MenuItem value="sql">SQL</MenuItem>
              <MenuItem value="json">JSON</MenuItem>
            </Select>
          </Box>
          <Tooltip title="Copy">
            <IconButton size="small" onClick={handleCopy}>
              {copied ? <CheckIcon color="success" /> : <ContentCopyIcon />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Paste">
            <IconButton size="small" onClick={handlePaste}>
              {paste ? <CheckIcon color="success" /> : <ContentPasteIcon />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box className={classes.editorContainer}>
        <CodeMirror
          value={code}
          height="355px"
          extensions={[getLanguageExtension(language)]}
          theme={isDarkMode ? githubDark : githubLight}
          onChange={handleChange}
          className={classes.codeMirror}
          ref={viewRef}
        />
      </Box>
    </Box>
  );
};

export default CodeEditorV2;
